import React, { useRef, useState } from "react";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/Layout";
import { downloadElementsAsPdf } from "@/lib/pdf";
import { GUIDE_INTRO, PRINCIPLES, STOP_STRATEGY, FIVE_QUESTIONS, PHRASES, PRIORITIES } from "@/data/superCerveauGuide";

const Page = ({ innerRef, children }) => (
  <div ref={innerRef} className="bg-white text-[#4A3B36] p-8" style={{ width: 794, minHeight: 1050, fontFamily: "Georgia, serif" }}>
    <div className="flex items-center justify-between mb-5 border-b border-[#E5DCD1] pb-3"><div className="text-xs tracking-[0.25em] font-bold text-[#B58C67]">M2HAT PSYCHOLOGIE · GUIDE PARENTS</div><div className="text-xs text-[#6E5C53]">Préconisations environnementales — TDAH enfant</div></div>
    {children}
  </div>
);
const Principle = ({ p }) => (
  <div className="rounded-xl border border-[#E5DCD1] p-3 mb-3 text-xs break-inside-avoid">
    <div className="font-bold text-sm mb-1">{p.n}. {p.title}</div>
    <p className="mb-1.5">{p.why}</p>
    {p.tools && <p className="mb-1.5"><strong>Outils :</strong> {p.tools.join(" · ")}</p>}
    {p.avoid && <p className="mb-1"><strong>✗ À éviter :</strong> <em>{p.avoid}</em></p>}
    {p.prefer && <p className="mb-1"><strong>✓ À privilégier :</strong> {p.prefer.join(" — ")}</p>}
    {p.example && <p className="mb-1"><strong>{p.example.title} :</strong> {p.example.items.join(" → ")}</p>}
    {p.quote && <p className="italic text-[#B58C67]">{p.quote}</p>}
  </div>
);

export const GuidePdfButton = () => {
  const refs = [useRef(null), useRef(null), useRef(null)];
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await downloadElementsAsPdf(refs.map((r) => r.current), "guide-parents-tdah-10-principes.pdf"); toast.success("Guide parents exporté en PDF"); }
    catch { toast.error("Export PDF impossible"); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={run} disabled={busy} data-testid="sc-guide-pdf" className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors disabled:opacity-50"><FileDown size={16} /> {busy ? "Préparation…" : "Télécharger le guide (PDF)"}</button>
      <div className="fixed -left-[9999px] top-0" aria-hidden="true">
        <Page innerRef={refs[0]}>
          <div className="text-center mb-5"><Brand /></div>
          <h1 className="text-2xl font-bold text-center mb-1">Accompagner au quotidien un enfant présentant un TDAH</h1>
          <p className="text-center text-sm text-[#6E5C53] mb-5">Guide pratique à destination des parents — les 10 principes à retenir</p>
          <h2 className="font-bold mb-1">{GUIDE_INTRO.title}</h2>
          <p className="text-sm mb-3">{GUIDE_INTRO.text}</p>
          <p className="text-sm italic rounded-xl bg-[#EFE7DD] p-3 mb-5">{GUIDE_INTRO.key}</p>
          {PRINCIPLES.slice(0, 4).map((p) => <Principle key={p.n} p={p} />)}
        </Page>
        <Page innerRef={refs[1]}>{PRINCIPLES.slice(4).map((p) => <Principle key={p.n} p={p} />)}</Page>
        <Page innerRef={refs[2]}>
          <h2 className="font-bold text-lg mb-2">STOP – RÉFLÉCHIR – AGIR</h2>
          <div className="grid grid-cols-3 gap-3 mb-5 text-xs">{STOP_STRATEGY.map((s) => <div key={s.t} className="rounded-xl bg-[#EFE7DD] p-3"><div className="font-black tracking-widest mb-1">{s.t}</div>{s.d}</div>)}</div>
          <h2 className="font-bold text-lg mb-2">Quand l'enfant bloque : la grille « 5 questions »</h2>
          <ol className="list-decimal pl-5 text-sm mb-5">{FIVE_QUESTIONS.map((q) => <li key={q}>{q}</li>)}</ol>
          <h2 className="font-bold text-lg mb-2">Phrases à remplacer</h2>
          <table className="w-full text-xs border-collapse mb-5"><thead><tr className="bg-[#EFE7DD]"><th className="p-2 text-left border border-[#E5DCD1]">À éviter</th><th className="p-2 text-left border border-[#E5DCD1]">À privilégier</th></tr></thead><tbody>{PHRASES.map(([a, b]) => <tr key={a}><td className="p-2 border border-[#E5DCD1]">{a}</td><td className="p-2 border border-[#E5DCD1]">{b}</td></tr>)}</tbody></table>
          <h2 className="font-bold text-lg mb-2">Les priorités à mettre en place en premier</h2>
          <ol className="list-decimal pl-5 text-sm mb-3">{PRIORITIES.map((p) => <li key={p}>{p}</li>)}</ol>
          <p className="text-xs text-[#6E5C53]">Ne pas multiplier les outils : 1 ou 2 difficultés prioritaires → 1 stratégie → une période d'essai → on évalue, on garde ou on ajuste.</p>
          <p className="mt-8 text-[10px] text-[#6E5C53] text-center">M2HAT Psychologie — Bilan cognitif · Évaluation et suivi thérapeutique. Document destiné à accompagner les parents dans la mise en place d'adaptations concrètes au quotidien.</p>
        </Page>
      </div>
    </>
  );
};
